import { Link, useNavigate } from 'react-router';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { Button, StatusPill } from '../../components/ui';
import { RECENT_RUNS, useRuns } from '../../hooks/queries';
import { useAuth } from '../../stores/auth';
import { formatDate, formatDistance } from '../../lib/format';
import { RunList } from './RunList';
import { useSync } from '../../services/sync';
import { useT } from '../../i18n';

export function RunHistory() {
  const t = useT();
  // Only the latest few here; "See all" opens the full, paged list.
  const q = useRuns(RECENT_RUNS);
  const me = useAuth((s) => s.user?.id);
  const navigate = useNavigate();
  const queued = useSync((s) => s.queued).filter((r) => r.status !== 'synced' && r.status !== 'active' && r.status !== 'paused');

  return (
    <div data-testid="run-history">
      {queued.length ? (
        <ul className="mb-3 space-y-2">
          {queued.map((r) => {
            const d = formatDistance(r.clientDistanceM);
            return (
              <li key={r.id}>
                <Link to={`/runs/${r.id}`} className="flex items-center justify-between rounded-2xl border border-dashed border-ink-600 px-4 py-3">
                  <span>
                    <span className="block font-semibold">{formatDate(new Date(r.startedAt).toISOString())}</span>
                    <span className="text-xs text-ink-400">
                      {d.value} {d.unit} · {t(r.status === 'failed' ? 'Sync failed' : 'Saved on device, waiting to sync')}
                    </span>
                  </span>
                  <StatusPill status={r.status === 'failed' ? 'REJECTED' : 'LOCAL'} />
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
      {q.isPending ? (
        <LoadingState />
      ) : q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : (q.data?.pages[0]?.items.length ?? 0) === 0 && queued.length === 0 ? (
        <EmptyState icon="🏃" title={t('No runs yet')} body={t('Your runs, with distance, pace, XP and captures, will show up here.')} />
      ) : (
        <RunList runs={q.data?.pages[0]?.items ?? []} />
      )}
      {q.data?.pages[0]?.nextCursor && me ? (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => navigate(`/players/${me}/runs`)} data-testid="see-all-runs">
          {t('See all runs')}
        </Button>
      ) : null}
    </div>
  );
}
